import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType, IVisibleLogic, SettingKey } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getSetting, updateSetting } from 'services'
import { Button, InputVisibleLogic } from 'stories/components'
import { confirm, ConvertInputValue, InputConvert, InputValidate, isEmpty } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  defaultInputs,
  DocumentGenerateLimitCategory,
  emptyLimitRules,
  IDocumentGenerateDefaultsConfig,
  IDocumentGenerateLimitRule,
  limitCategories,
  limitRuleInputsFor,
  newLimitRule,
  parseDocumentGenerateDefaults,
} from './constants'

export const DocumentGenerateConfig = () => {
  const [action, setAction] = useState('')
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [rules, setRules] = useState<Record<DocumentGenerateLimitCategory, IDocumentGenerateLimitRule[]>>(
    emptyLimitRules(),
  )
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    setAction('setting')
    getSetting(SettingKey.DOCUMENT_GENERATE_DEFAULTS)
      .then(({ value }) => {
        const content = parseDocumentGenerateDefaults(JSON.parse(value || '{}'))
        let newInputs = cloneDeep(inputs)
        Object.keys(inputs).forEach((key) => {
          if (content.defaults[key] !== undefined && content.defaults[key] !== null) {
            newInputs[key].value = content.defaults[key]
          }
        })
        setInputs(newInputs)
        setRules(content.rules || emptyLimitRules())
        setChanged(false)
      })
      .finally(() => setAction(''))
  }, [])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    setChanged(true)
  }

  const onAddRule = (category: DocumentGenerateLimitCategory) => {
    setRules((prev) => {
      const next = cloneDeep(prev)
      next[category] = [...(next[category] || []), newLimitRule()]
      return next
    })
    setChanged(true)
  }

  const onRemoveRule = async (category: DocumentGenerateLimitCategory, ruleIndex: number) => {
    const ok = await confirm('Are you sure you want to remove this rule?')
    if (!ok) return
    setRules((prev) => {
      const next = cloneDeep(prev)
      next[category].splice(ruleIndex, 1)
      return next
    })
    setChanged(true)
  }

  const onChangeRuleLogic = (category: DocumentGenerateLimitCategory, ruleIndex: number, logic: IVisibleLogic[][]) => {
    setRules((prev) => {
      const next = cloneDeep(prev)
      if (!next[category]?.[ruleIndex]) return prev
      next[category][ruleIndex].visibleLogic = logic
      return next
    })
    setChanged(true)
  }

  const onChangeRuleValue = (
    category: DocumentGenerateLimitCategory,
    ruleIndex: number,
    key: 'min' | 'max',
    value: string,
  ) => {
    setRules((prev) => {
      const next = cloneDeep(prev)
      if (!next[category]?.[ruleIndex]) return prev
      const fieldInputs = limitRuleInputsFor(category)
      next[category][ruleIndex][key] = InputConvert(fieldInputs[key], value)
      return next
    })
    setChanged(true)
  }

  const buildPayload = (): IDocumentGenerateDefaultsConfig | null => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    const defaults: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      defaults[key] = ConvertInputValue(newInputs[key], newInputs[key].value)
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return null
    return { defaults, rules }
  }

  const onSave = async () => {
    if (!changed) return
    const payload = buildPayload()
    if (!payload) return

    setAction('setting')
    await updateSetting(SettingKey.DOCUMENT_GENERATE_DEFAULTS, JSON.stringify(payload))
    setAction('')
    setChanged(false)
    toast('Successfully saved', { type: 'success' })
  }

  const onBlurDefaults = async () => {
    if (!changed) return
    await onSave()
  }

  const renderRule = (category: DocumentGenerateLimitCategory, rule: IDocumentGenerateLimitRule, ruleIndex: number) => {
    const ruleKey = `${category}-rule-${ruleIndex}`
    const fieldInputs = limitRuleInputsFor(category)
    return (
      <div className="border rounded-md p-3 mb-3 bg-white" key={ruleKey}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">Rule {ruleIndex + 1}</p>
          <span className="btn-icon cursor-pointer" onClick={() => onRemoveRule(category, ruleIndex)}>
            <TrashIcon className="w-4 h-4 text-red-500" />
          </span>
        </div>

        <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {(['min', 'max'] as const).map((key) => {
            const input: InputType = { ...fieldInputs[key], value: rule[key] ?? '' } as InputType
            return (
              <div key={`${ruleKey}-${key}`}>
                <RenderInput
                  Key={`${ruleKey}-${key}`}
                  input={input}
                  onChange={(_k: string, value: string) => onChangeRuleValue(category, ruleIndex, key, value)}
                />
              </div>
            )
          })}
        </div>

        <InputVisibleLogic
          id={`${ruleKey}-logic`}
          title="Conditions (State / Product Type)"
          value={rule.visibleLogic || []}
          options={{ hasNotVisible: true, useMultiSelectForSelectInput: true }}
          onChange={(value: IVisibleLogic[][]) => onChangeRuleLogic(category, ruleIndex, value)}
        />
        <p className="text-xs text-gray-500 mt-2">
          Tip: choose Loan Structure → Product Type and/or Property State. Select fields support multi-select so one rule
          can cover multiple products or states. Leave the conditions empty to apply this rule to all loans.
        </p>
      </div>
    )
  }

  const renderCategory = (category: typeof limitCategories[number]) => {
    const categoryRules = rules[category.key] || []
    return (
      <div className="bg-slate-50 p-3 rounded-md mb-6" key={category.key}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">
            {category.title}
            <span className="text-xs text-gray-500 ml-2 font-normal">
              Limits the &quot;{category.fieldTitle}&quot; field on every matching loan. First matching rule wins.
              Multi-select Product Type / Property State to reuse limits across products or states.
            </span>
          </p>
          <span
            className="cursor-pointer text-shade-blue"
            onClick={() => onAddRule(category.key)}
            title={`Add ${category.title} Rule`}
          >
            <PlusIcon className="w-5 h-5" />
          </span>
        </div>

        {!categoryRules.length && (
          <div className="text-sm text-gray-500 py-4 text-center">
            No {category.title.toLowerCase()} rules yet.{' '}
            <Button link onClick={() => onAddRule(category.key)}>
              Add a rule
            </Button>
          </div>
        )}

        {categoryRules.map((rule, index) => renderRule(category.key, rule, index))}
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={!isEmpty(action)} />
      <SaveChanges show={changed} onSave={onSave} />

      <p className="text-sm text-gray-600 mb-4">
        These values are used as defaults on the Document Generate page when a field is empty. Conditional limits below
        are enforced on every loan - existing or new - whose state / product type matches the rule.
      </p>

      <div className="relative mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(inputs).map((key) => {
          const input = inputs[key]
          return (
            <div className={`input md:col-span-${input.span || 1}`} key={key}>
              <RenderInput input={input} Key={key} onChange={onChange} onBlur={onBlurDefaults} />
            </div>
          )
        })}
      </div>

      <h3 className="text-base font-semibold mb-3">Conditional Limits</h3>
      {limitCategories.map((category) => renderCategory(category))}
    </div>
  )
}
